import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Play, Pause, RotateCcw, Volume2, VolumeX, Bell,
  Flame, Calendar as CalendarIcon, NotebookPen, X,
} from "lucide-react";
import { api } from "../../lib/api";

const DURATIONS = [5, 10, 15, 20, 30, 45];
const MODES = [
  { id: "silent", label: "Silent", note: "Pure stillness · no sound" },
  { id: "bell", label: "Bell intervals", note: "Bell rings every 5 minutes" },
  { id: "breath", label: "Breath pacer", note: "Visual 4-4-6 breath cycle" },
];

const BELL_AUDIO = "https://upload.wikimedia.org/wikipedia/commons/8/8e/Singing_bowl.ogg";
const SESSIONS_KEY = "bhimsangha:meditation:sessions";

function fmt(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m.toString().padStart(2, "0")}:${r.toString().padStart(2, "0")}`;
}

function dayKey(d = new Date()) {
  return d.toISOString().slice(0, 10);
}

function calculateStreak(sessions) {
  if (!sessions.length) return 0;
  const days = new Set(sessions.map((s) => s.day));
  let streak = 0;
  const today = new Date();
  // Allow today OR yesterday to start the streak (so a streak isn't broken until a full day passes)
  let cursor = new Date(today);
  if (!days.has(dayKey(cursor))) {
    cursor.setDate(cursor.getDate() - 1);
    if (!days.has(dayKey(cursor))) return 0;
  }
  while (days.has(dayKey(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

export default function PlatformMeditation() {
  const [duration, setDuration] = useState(15);
  const [mode, setMode] = useState("silent");
  const [secondsLeft, setSecondsLeft] = useState(15 * 60);
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [sessions, setSessions] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SESSIONS_KEY) || "[]");
    } catch { return []; }
  });
  const [serverStats, setServerStats] = useState(null); // {streak,total_sessions,total_minutes}
  const [reflectionOpen, setReflectionOpen] = useState(false);
  const [pendingSession, setPendingSession] = useState(null);
  const [reflectionText, setReflectionText] = useState("");
  const [breathPhase, setBreathPhase] = useState("inhale");
  const intervalRef = useRef(null);
  const breathRef = useRef(null);
  const bellRef = useRef(null);

  useEffect(() => {
    bellRef.current = new Audio(BELL_AUDIO);
    bellRef.current.volume = 0.55;
  }, []);

  // Hydrate from server on mount (overrides localStorage as source of truth)
  useEffect(() => {
    let alive = true;
    api.get("/platform/meditation/sessions")
      .then((r) => {
        if (!alive) return;
        const server = Array.isArray(r.data?.sessions) ? r.data.sessions : [];
        // Map server schema → UI schema (id used as React key)
        const mapped = server.map((s) => ({
          id: s.session_id || s.ts,
          day: s.day,
          minutes: s.minutes,
          mode: s.mode,
          reflection: s.reflection || "",
          ts: s.ts,
        }));
        setSessions(mapped);
        setServerStats(r.data?.stats || null);
        localStorage.setItem(SESSIONS_KEY, JSON.stringify(mapped));
      })
      .catch(() => { /* keep localStorage seed */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
  }, [sessions]);

  useEffect(() => {
    if (!running) setSecondsLeft(duration * 60);
  }, [duration, running]);

  // main timer
  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(intervalRef.current);
          setRunning(false);
          ringBell();
          finalizeSession();
          return 0;
        }
        if (mode === "bell" && (duration * 60 - (s - 1)) % (5 * 60) === 0) {
          ringBell();
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, mode, duration]);

  // Breath pacer (4 in · 4 hold · 6 out)
  useEffect(() => {
    if (!running || mode !== "breath") return;
    const cycle = [
      { phase: "inhale", ms: 4000 },
      { phase: "hold", ms: 4000 },
      { phase: "exhale", ms: 6000 },
    ];
    let idx = 0;
    const tick = () => {
      setBreathPhase(cycle[idx].phase);
      breathRef.current = setTimeout(() => {
        idx = (idx + 1) % cycle.length;
        tick();
      }, cycle[idx].ms);
    };
    tick();
    return () => {
      if (breathRef.current) clearTimeout(breathRef.current);
    };
  }, [running, mode]);

  const ringBell = () => {
    if (muted || !bellRef.current) return;
    try {
      bellRef.current.currentTime = 0;
      bellRef.current.play().catch(() => {});
    } catch (e) { /* ignore */ }
  };

  const finalizeSession = () => {
    const minutes = duration;
    const newSession = {
      id: Date.now(),
      day: dayKey(),
      minutes,
      mode,
      reflection: "",
      ts: new Date().toISOString(),
    };
    setPendingSession(newSession);
    setReflectionOpen(true);
  };

  const persistSession = async (session) => {
    try {
      const r = await api.post("/platform/meditation/sessions", {
        minutes: session.minutes,
        mode: session.mode,
        reflection: session.reflection || "",
      });
      if (r.data?.stats) setServerStats(r.data.stats);
      // Replace local placeholder with server-canonical row (uses session_id)
      const saved = r.data?.session;
      if (saved) {
        setSessions((prev) => {
          const idx = prev.findIndex((s) => s.id === session.id);
          if (idx === -1) return prev;
          const next = [...prev];
          next[idx] = {
            id: saved.session_id || saved.ts,
            day: saved.day,
            minutes: saved.minutes,
            mode: saved.mode,
            reflection: saved.reflection || "",
            ts: saved.ts,
          };
          return next;
        });
      }
    } catch { /* offline → localStorage already updated */ }
  };

  const saveReflection = () => {
    if (!pendingSession) return;
    const final = { ...pendingSession, reflection: reflectionText.trim() };
    setSessions((prev) => [final, ...prev].slice(0, 200));
    persistSession(final);
    setReflectionText("");
    setPendingSession(null);
    setReflectionOpen(false);
  };

  const skipReflection = () => {
    if (pendingSession) {
      setSessions((prev) => [pendingSession, ...prev].slice(0, 200));
      persistSession(pendingSession);
    }
    setReflectionText("");
    setPendingSession(null);
    setReflectionOpen(false);
  };

  const start = () => {
    if (secondsLeft === 0) setSecondsLeft(duration * 60);
    setRunning(true);
    ringBell();
  };
  const pause = () => setRunning(false);
  const reset = () => {
    setRunning(false);
    setSecondsLeft(duration * 60);
  };

  const totalSecs = duration * 60;
  const progress = totalSecs ? ((totalSecs - secondsLeft) / totalSecs) * 100 : 0;

  // Stats — prefer server-computed when available, else compute locally
  const localStreak = useMemo(() => calculateStreak(sessions), [sessions]);
  const localTotalMinutes = useMemo(() => sessions.reduce((sum, s) => sum + (s.minutes || 0), 0), [sessions]);
  const streak = serverStats?.streak ?? localStreak;
  const totalMinutes = serverStats?.total_minutes ?? localTotalMinutes;
  const totalSessions = serverStats?.total_sessions ?? sessions.length;
  const recent = sessions.slice(0, 5);

  const breathScale = breathPhase === "inhale" ? 1.18 : breathPhase === "exhale" ? 0.82 : 1.0;
  const breathLabel = { inhale: "Breathe in", hold: "Hold", exhale: "Breathe out" }[breathPhase];

  return (
    <div
      className="min-h-[calc(100vh-3.5rem)] lg:min-h-screen bg-gradient-to-br from-[#0B1F4D] via-[#1a2f6b] to-[#6C4EFF] text-pearl py-10 px-4 sm:px-8"
      data-testid="platform-meditation"
    >
      <div className="max-w-5xl mx-auto">
        <p className="text-[10px] uppercase tracking-[0.3em] text-saffron text-center">Meditation Room</p>
        <h1 className="font-display text-4xl sm:text-5xl text-pearl text-center mt-2">
          A quiet seat for your practice
        </h1>
        <p className="text-center text-pearl/70 max-w-xl mx-auto mt-3 text-sm leading-relaxed italic">
          "Mind precedes all things. Mind is their chief; they are mind-made."
          <span className="block mt-1 not-italic text-pearl/45">— Dhammapada, verse 1</span>
        </p>

        {/* Stats strip */}
        <div className="mt-10 grid grid-cols-3 gap-3 max-w-2xl mx-auto" data-testid="meditation-stats">
          <Stat icon={Flame} label="Day streak" value={streak} highlight />
          <Stat icon={CalendarIcon} label="Total sessions" value={totalSessions} />
          <Stat icon={NotebookPen} label="Minutes sat" value={totalMinutes} />
        </div>

        {/* Big timer dial */}
        <div className="mt-12 flex flex-col items-center" data-testid="meditation-timer">
          <div className="relative w-72 h-72 sm:w-80 sm:h-80">
            <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="46" stroke="rgba(255,255,255,0.08)" strokeWidth="2.5" fill="none" />
              <circle
                cx="50" cy="50" r="46"
                stroke="#D4A64A" strokeWidth="2.5" fill="none"
                strokeLinecap="round"
                strokeDasharray="289"
                strokeDashoffset={289 - (289 * progress) / 100}
                style={{ transition: "stroke-dashoffset 1s linear" }}
              />
            </svg>

            {/* Breath pacer ring */}
            {running && mode === "breath" && (
              <div
                className="absolute inset-6 rounded-full border-2 border-saffron/40 pointer-events-none"
                style={{
                  transform: `scale(${breathScale})`,
                  transition: "transform 4s ease-in-out",
                  boxShadow: "0 0 60px rgba(212,166,74,0.25)",
                }}
                data-testid="breath-ring"
              />
            )}

            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-6xl sm:text-7xl font-display text-pearl tabular-nums" data-testid="timer-display">
                {fmt(secondsLeft)}
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-pearl/50 mt-2">
                {running
                  ? mode === "breath"
                    ? breathLabel
                    : "Sitting"
                  : secondsLeft === 0
                  ? "Complete · 🪷"
                  : "Ready"}
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="mt-10 flex items-center gap-3">
            <button
              onClick={reset}
              data-testid="timer-reset"
              className="p-3 rounded-full bg-white/5 border border-white/10 text-pearl/70 hover:bg-white/10"
              aria-label="Reset"
            >
              <RotateCcw size={18} />
            </button>
            {running ? (
              <button
                onClick={pause}
                data-testid="timer-pause"
                className="px-7 py-3.5 rounded-full bg-saffron text-navy font-medium text-sm hover:bg-saffron/90 inline-flex items-center gap-2 shadow-lg shadow-saffron/20"
              >
                <Pause size={16} /> Pause
              </button>
            ) : (
              <button
                onClick={start}
                data-testid="timer-start"
                className="px-7 py-3.5 rounded-full bg-saffron text-navy font-medium text-sm hover:bg-saffron/90 inline-flex items-center gap-2 shadow-lg shadow-saffron/20"
              >
                <Play size={16} /> {secondsLeft === 0 ? "Sit again" : "Begin"}
              </button>
            )}
            <button
              onClick={() => setMuted((m) => !m)}
              data-testid="timer-mute"
              className="p-3 rounded-full bg-white/5 border border-white/10 text-pearl/70 hover:bg-white/10"
              aria-label="Mute"
            >
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
          </div>
        </div>

        {/* Settings */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid="duration-picker">
            <h3 className="text-xs uppercase tracking-[0.25em] text-saffron mb-3">Duration</h3>
            <div className="flex flex-wrap gap-2">
              {DURATIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => !running && setDuration(d)}
                  disabled={running}
                  data-testid={`duration-${d}`}
                  className={`px-4 py-1.5 rounded-md text-sm border transition-colors disabled:opacity-40 ${
                    duration === d
                      ? "bg-saffron text-navy border-saffron"
                      : "bg-transparent text-pearl/80 border-white/15 hover:border-saffron"
                  }`}
                >
                  {d} min
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid="mode-picker">
            <h3 className="text-xs uppercase tracking-[0.25em] text-saffron mb-3 inline-flex items-center gap-2">
              <Bell size={12} /> Mode
            </h3>
            <div className="space-y-2">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  onClick={() => !running && setMode(m.id)}
                  disabled={running}
                  data-testid={`mode-${m.id}`}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm border transition-colors disabled:opacity-40 ${
                    mode === m.id
                      ? "bg-saffron/15 text-pearl border-saffron/50"
                      : "bg-transparent text-pearl/75 border-white/10 hover:border-saffron/40"
                  }`}
                >
                  <div className="font-medium">{m.label}</div>
                  <div className="text-[11px] text-pearl/50 mt-0.5">{m.note}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Recent sessions / journal */}
        {recent.length > 0 && (
          <div className="mt-12 bg-white/5 border border-white/10 rounded-xl p-6" data-testid="meditation-journal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs uppercase tracking-[0.25em] text-saffron inline-flex items-center gap-2">
                <NotebookPen size={12} /> Recent sessions
              </h3>
              {sessions.length > 1 && (
                <button
                  onClick={() => {
                    if (window.confirm("Clear all meditation history? This cannot be undone.")) {
                      setSessions([]);
                      setServerStats({ streak: 0, total_sessions: 0, total_minutes: 0 });
                      api.delete("/platform/meditation/sessions").catch(() => {});
                    }
                  }}
                  data-testid="clear-history"
                  className="text-[11px] text-pearl/50 hover:text-saffron"
                >
                  Clear history
                </button>
              )}
            </div>
            <div className="space-y-3">
              {recent.map((s) => (
                <div
                  key={s.id}
                  className="flex gap-4 p-3 rounded-lg bg-white/5 border border-white/5"
                  data-testid={`session-${s.id}`}
                >
                  <div className="w-12 h-12 rounded-lg bg-saffron/15 border border-saffron/30 flex flex-col items-center justify-center flex-shrink-0">
                    <div className="font-display text-xl text-saffron leading-none">{s.minutes}</div>
                    <div className="text-[9px] uppercase tracking-widest text-saffron/70">min</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-pearl">
                      {new Date(s.ts).toLocaleString("en-GB", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-pearl/40">
                        {s.mode}
                      </span>
                    </div>
                    {s.reflection && (
                      <p className="mt-1 text-sm text-pearl/70 italic leading-snug">"{s.reflection}"</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="mt-12 text-center text-[11px] text-pearl/40 italic">
          Sabbe sattā sukhitā hontu, may all beings be happy.
        </p>
      </div>

      {/* Reflection modal */}
      {reflectionOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
          data-testid="reflection-modal"
        >
          <div className="bg-[#0B1F4D] border border-white/15 rounded-2xl max-w-md w-full p-7 text-pearl shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-saffron">Sit complete · 🪷</p>
                <h3 className="font-display text-2xl text-pearl mt-1">A short reflection?</h3>
              </div>
              <button
                onClick={skipReflection}
                aria-label="Close"
                className="p-1 text-pearl/50 hover:text-pearl"
              >
                <X size={18} />
              </button>
            </div>
            <p className="mt-3 text-sm text-pearl/70 leading-relaxed">
              How was the sit? What did you notice? Optional, write a line or skip.
            </p>
            <textarea
              value={reflectionText}
              onChange={(e) => setReflectionText(e.target.value)}
              data-testid="reflection-input"
              placeholder="The mind was restless at first, then settled around the breath…"
              rows={4}
              className="mt-4 w-full bg-white/5 border border-white/15 rounded-md p-3 text-sm text-pearl placeholder-pearl/30 focus:outline-none focus:border-saffron"
            />
            <div className="mt-5 flex gap-3 justify-end">
              <button
                onClick={skipReflection}
                data-testid="reflection-skip"
                className="px-4 py-2 text-sm text-pearl/70 hover:text-pearl"
              >
                Skip
              </button>
              <button
                onClick={saveReflection}
                data-testid="reflection-save"
                className="px-5 py-2 bg-saffron text-navy text-sm font-medium rounded-md hover:bg-saffron/90"
              >
                Save reflection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, highlight }) {
  return (
    <div
      className={`rounded-xl p-4 border text-center ${
        highlight
          ? "bg-saffron/10 border-saffron/30"
          : "bg-white/5 border-white/10"
      }`}
    >
      <Icon size={16} className={`mx-auto ${highlight ? "text-saffron" : "text-pearl/50"}`} />
      <div className={`mt-2 font-display text-2xl ${highlight ? "text-saffron" : "text-pearl"}`}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-pearl/55 mt-0.5">{label}</div>
    </div>
  );
}
